import { useId, useState } from "react";
import type { BuyerInterviewDto, BuyerInterviewRegistryDto } from "@/api/buyers";
import { Select } from "@/components/ui/Select";
import { Skeleton, StatusBadge } from "@/design-system/components/ui";
import { InlineQueryError } from "../queryStates";
import { buyerLabel } from "../buyerFilters";
import { verdictTone } from "../buyerListBits";
import { compareInterviews, type CompareCategory } from "./interviewCompare";
import {
  recordedWordingVersion,
  useBuyerInterviewVersion,
  useInterviewRegistry,
} from "./useBuyerInterview";

/**
 * This interview against another completed one. Older version on the left.
 * Per category: the computed result on each side, the answers that read
 * differently side by side, and the unchanged ones collapsed.
 *
 * <p>Each side renders in the wording it was recorded with: `registry` is the
 * current side's, and the other side's is loaded for ITS `wordingVersion`.
 * Where a question was worded differently, each side shows its own label.</p>
 */
export function InterviewCompare({
  buyerLeadId,
  current,
  registry,
  versions,
  compareWith,
  onCompareWith,
}: {
  buyerLeadId: string;
  current: BuyerInterviewDto;
  /** The registry in `current`'s own wording version. */
  registry: BuyerInterviewRegistryDto;
  /** Every OTHER completed version, newest first. */
  versions: number[];
  compareWith: number;
  onCompareWith: (version: number) => void;
}) {
  const pickerId = useId();
  const otherQuery = useBuyerInterviewVersion(buyerLeadId, compareWith);
  const other = otherQuery.data?.status === "Completed" ? otherQuery.data : null;
  const otherRegistryQuery = useInterviewRegistry(
    other ? recordedWordingVersion(other) : 0,
    other != null,
  );
  const otherRegistry = otherRegistryQuery.data;

  const otherIsLeft = other != null && other.version < current.version;
  const [left, right] = otherIsLeft ? [other, current] : [current, other];
  const [leftRegistry, rightRegistry] = otherIsLeft
    ? [otherRegistry, registry]
    : [registry, otherRegistry];

  return (
    <div className="grid gap-space-4" data-testid="interview-compare">
      <div className="flex flex-wrap items-center gap-space-2">
        <label htmlFor={pickerId} className="text-caption uppercase tracking-wide text-gray-mid">
          Compare with
        </label>
        <Select
          id={pickerId}
          className="w-auto"
          value={compareWith}
          onChange={(e) => onCompareWith(Number(e.target.value))}
          data-testid="interview-compare-picker"
        >
          {versions.map((v) => (
            <option key={v} value={v}>
              v{v}
            </option>
          ))}
        </Select>
      </div>

      {(otherQuery.isLoading || otherRegistryQuery.isLoading) && (
        <Skeleton className="h-40 w-full" />
      )}
      {(otherQuery.isError || otherRegistryQuery.isError) && (
        <InlineQueryError
          what={`interview v${compareWith}`}
          onRetry={() => {
            void otherQuery.refetch();
            void otherRegistryQuery.refetch();
          }}
          retrying={otherQuery.isFetching || otherRegistryQuery.isFetching}
        />
      )}
      {!otherQuery.isLoading && !otherQuery.isError && otherQuery.data && !other && (
        <p className="text-small text-gray-mid m-0">
          Interview v{compareWith} has not been completed, so there is nothing to compare.
        </p>
      )}

      {other && right && leftRegistry && rightRegistry && (
        <>
          <OverallLine left={left} right={right} />
          {compareInterviews(left, right, leftRegistry, rightRegistry).map((category) => (
            <CategoryCompare
              key={category.dimension}
              category={category}
              leftVersion={left.version}
              rightVersion={right.version}
            />
          ))}
        </>
      )}
    </div>
  );
}

function FinalResult({ interview }: { interview: BuyerInterviewDto }) {
  const outcome = interview.outcome;
  if (!outcome) return <span className="text-gray-mid">Not recorded</span>;
  return (
    <span className="flex flex-wrap items-center gap-space-2">
      <StatusBadge tone={verdictTone(outcome.effectiveVerdict)}>
        {buyerLabel(outcome.effectiveVerdict)}
      </StatusBadge>
      {outcome.overriddenVerdict != null && (
        <span className="text-caption text-gray-mid">
          overridden from {buyerLabel(outcome.computedVerdict)}
        </span>
      )}
    </span>
  );
}

function OverallLine({ left, right }: { left: BuyerInterviewDto; right: BuyerInterviewDto }) {
  return (
    <div
      className="grid grid-cols-2 gap-space-3 rounded-md border border-border-light p-space-3"
      data-testid="interview-compare-overall"
    >
      {[left, right].map((interview) => (
        <div key={interview.version} className="grid gap-space-1">
          <span className="text-caption uppercase tracking-wide text-gray-mid">
            v{interview.version} result
          </span>
          <FinalResult interview={interview} />
        </div>
      ))}
    </div>
  );
}

function VerdictCell({ verdict }: { verdict: CompareCategory["left"] }) {
  return verdict ? (
    <StatusBadge tone={verdictTone(verdict)}>{buyerLabel(verdict)}</StatusBadge>
  ) : (
    <span className="text-caption text-gray-mid">Not recorded</span>
  );
}

function CategoryCompare({
  category,
  leftVersion,
  rightVersion,
}: {
  category: CompareCategory;
  leftVersion: number;
  rightVersion: number;
}) {
  const [showUnchanged, setShowUnchanged] = useState(false);
  const changed = category.answers.filter((a) => a.changed);
  const unchanged = category.answers.filter((a) => !a.changed);

  return (
    <section
      className="grid gap-space-2"
      data-testid={`interview-compare-section-${category.dimension}`}
    >
      <div className="flex flex-wrap items-center gap-space-2">
        <h3 className="font-display font-bold text-small text-onyx m-0">
          {category.rightTitle}
        </h3>
        {category.leftTitle !== category.rightTitle && (
          <span
            className="text-caption text-gray-mid"
            data-testid="interview-compare-left-title"
          >
            (v{leftVersion}: {category.leftTitle})
          </span>
        )}
        <VerdictCell verdict={category.left} />
        <span className="text-caption text-gray-mid" aria-hidden>
          →
        </span>
        <VerdictCell verdict={category.right} />
        <span className="text-caption text-gray-mid" data-testid="interview-compare-result-change">
          {category.resultChanged ? "Result changed" : "Result unchanged"}
        </span>
      </div>

      {changed.length > 0 && (
        <AnswerTable
          rows={changed}
          leftVersion={leftVersion}
          rightVersion={rightVersion}
          testId="interview-compare-changed"
        />
      )}
      {changed.length === 0 && (
        <p className="text-caption text-gray-mid m-0">No answers changed.</p>
      )}

      {unchanged.length > 0 && (
        <div>
          <button
            type="button"
            className="text-caption text-gold-shadow hover:underline"
            aria-expanded={showUnchanged}
            onClick={() => setShowUnchanged((v) => !v)}
            data-testid="interview-compare-toggle-unchanged"
          >
            {showUnchanged ? "Hide" : "Show"} {unchanged.length} unchanged{" "}
            {unchanged.length === 1 ? "answer" : "answers"}
          </button>
          {showUnchanged && (
            <AnswerTable
              rows={unchanged}
              leftVersion={leftVersion}
              rightVersion={rightVersion}
              testId="interview-compare-unchanged"
            />
          )}
        </div>
      )}
    </section>
  );
}

function AnswerTable({
  rows,
  leftVersion,
  rightVersion,
  testId,
}: {
  rows: CompareCategory["answers"];
  leftVersion: number;
  rightVersion: number;
  testId: string;
}) {
  return (
    <dl className="grid gap-space-2 m-0" data-testid={testId}>
      {rows.map((row) => {
        // One label when both wordings asked it alike; otherwise each side's own.
        const relabelled = row.leftLabel !== row.rightLabel;
        return (
          <div
            key={row.questionId}
            className="border-b border-border-light pb-space-2 last:border-b-0"
            data-testid={`interview-compare-row-${row.questionId}`}
          >
            <dt className="text-caption text-gray-mid" data-testid="interview-compare-label">
              {relabelled ? (
                // Aligned over the two answer columns below.
                <span className="grid grid-cols-2 gap-space-3">
                  <span data-testid="interview-compare-left-label">{row.leftLabel}</span>
                  <span data-testid="interview-compare-right-label">{row.rightLabel}</span>
                </span>
              ) : (
                row.rightLabel
              )}
            </dt>
            <dd className="m-0 mt-space-1 grid grid-cols-2 gap-space-3 text-small text-onyx">
              <div>
                <span className="block text-caption uppercase tracking-wide text-gray-mid">
                  v{leftVersion}
                </span>
                <span className="whitespace-pre-wrap" data-testid="interview-compare-left">
                  {row.left}
                </span>
              </div>
              <div>
                <span className="block text-caption uppercase tracking-wide text-gray-mid">
                  v{rightVersion}
                </span>
                <span className="whitespace-pre-wrap" data-testid="interview-compare-right">
                  {row.right}
                </span>
              </div>
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
